import { useEffect, useMemo, useState } from "react";
import { EmptyList } from "src/components/EmptyList";
import Loading from "src/components/Loading";
import {
  NotifyMyDocumentsType,
  useSelectDocumentContext,
} from "src/pages/DashboardPage/acadia/notificationPage/SelectDocuments.mobile";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";

export default function NotifyTableWithFiltersMobile({
  documents,
  loading,
}: {
  documents: NotifyMyDocumentsType[];
  loading: boolean;
}) {
  const { items, addItems, addItem, removeItem, removeItems } =
    useSelectDocumentContext();
  const [checkedAll, setCheckedAll] = useState(false);

  function checkAll(state: boolean) {
    if (state) {
      addItems(documents);
    } else {
      removeItems(documents);
    }

    setCheckedAll(state);
  }

  function handleCheckItem(item: NotifyMyDocumentsType, isChecked: boolean) {
    if (isChecked) {
      addItem(item);
    } else {
      removeItem(item.id);
    }
  }

  useEffect(() => {
    const isEveryBodySelected =
      documents.length > 0 && documents.every((item) => items.has(item.id));

    setCheckedAll(isEveryBodySelected);
  }, [documents, items]);

  const sortedDocuments = useMemo(
    () =>
      [...documents].sort((a, b) =>
        items.has(a.id) > items.has(b.id) ? -1 : items.has(a.id) < items.has(b.id) ? 1 : 0,
      ),
    [documents, items],
  );

  if (loading) {
    return (
      <div className="py-6">
        <Loading />
      </div>
    );
  }

  if (!sortedDocuments.length) {
    return <EmptyList title="Документы не найдены" />;
  }

  return (
    <FlexCol className="divide-y">
      <button
        type="button"
        onClick={() => checkAll(!checkedAll)}
        className="flex items-center gap-3 px-6 py-4 text-left"
      >
        <DocumentCheckbox checked={checkedAll} />
        <span className="text-[16px] leading-[24px] text-[#111827]">
          Выбрать все
        </span>
      </button>

      {sortedDocuments.map((item) => (
        <DocumentRow
          key={item.id}
          item={item}
          isChecked={items.has(item.id)}
          onChange={handleCheckItem}
        />
      ))}
    </FlexCol>
  );
}

function DocumentRow({
  item,
  isChecked,
  onChange,
}: {
  item: NotifyMyDocumentsType;
  isChecked: boolean;
  onChange: (item: NotifyMyDocumentsType, isChecked: boolean) => void;
}) {
  const [checked, setChecked] = useState(isChecked);

  useEffect(() => {
    setChecked(isChecked);
  }, [isChecked]);

  function handleToggle(nextState: boolean) {
    setChecked(nextState);
    onChange(item, nextState);
  }

  return (
    <button
      type="button"
      onClick={() => handleToggle(!checked)}
      className="flex items-start gap-3 px-6 py-4 text-left"
    >
      <DocumentCheckbox checked={checked} />
      <FlexCol className="min-w-0 grow">
        <span className="text-[14px] leading-[20px] text-[#8899A8]">
          {item.code.code}
        </span>
        <span className="mt-1 break-words text-[14px] leading-[20px] text-darkblue">
          {item.code.name}
        </span>
        <span className="mt-1 text-[14px] leading-[20px] text-[#8899A8]">
          Дата публикации: {item.publicationDate}
        </span>
      </FlexCol>
    </button>
  );
}

function DocumentCheckbox({ checked }: { checked: boolean }) {
  return (
    <div
      className={twMerge(
        checked ? "border-darkblue" : "border-[#DFE4EA]",
        "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border bg-white",
      )}
    >
      <span className={twMerge(checked ? "opacity-100" : "opacity-0")}>
        <svg
          width="11"
          height="8"
          viewBox="0 0 11 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
            fill="#012169"
            stroke="#012169"
            strokeWidth="0.4"
          />
        </svg>
      </span>
    </div>
  );
}
